import React, { forwardRef, ReactNode, useImperativeHandle, useRef } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import useFormItemStatus from "antd/es/form/hooks/useFormItemStatus";
import Select, { RefSelectProps, SelectProps, SelectValue } from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import { useConfigProvider } from "../../ConfigProvider";
import ErrorRow from "../ErrorRow";
import Labeled, { LabeledProps } from "../Labeled";

const block = bem("mmc-select-field");

export interface SelectFieldProps<T extends SelectValue = SelectValue>
  extends Omit<SelectProps<T>, "className" | "ref">, Omit<LabeledProps, "children"> {
  caption?: ReactNode;
  error?: ReactNode;
  locked?: boolean;
  lockTooltip?: ReactNode;
  prefixIcon?: ReactNode;
  selectClassName?: string;
}

const SelectField = <T extends SelectValue = SelectValue>(
  {
    caption,
    className,
    disabled,
    error,
    extra,
    label,
    labelClassName,
    labelPosition,
    locked,
    lockTooltip,
    placeholder,
    prefixIcon,
    required,
    rowProps,
    selectClassName,
    showArrow,
    sideLabelSpan,
    size = "large",
    ...selectProps
  }: SelectFieldProps<T>,
  ref: React.Ref<SelectFieldComponent>
) => {
  const configProvider = useConfigProvider();
  const selectorRef = useRef<RefSelectProps>(null);
  const { status } = useFormItemStatus();

  useImperativeHandle(ref, () => ({
    blur: () => selectorRef.current?.blur(),
    focus: () => selectorRef.current?.focus(),
  }));

  return (
    <Labeled
      className={cn(
        block({
          disabled: disabled || locked,
          error: status === "error",
          hasIcon: !!prefixIcon,
        }),
        className
      )}
      extra={extra}
      label={label}
      labelClassName={cn(block("label"), labelClassName)}
      labelPosition={labelPosition}
      required={required}
      rowProps={rowProps}
      sideLabelSpan={sideLabelSpan}
    >
      <div className={block("select")}>
        <div className={block("select-row")}>
          {prefixIcon && <div className={block("icon-plate")}>{prefixIcon}</div>}
          <Select
            className={cn(block("select-field"), selectClassName)}
            disabled={disabled || locked}
            placeholder={placeholder ?? configProvider.formatMessage("ui.selectField.placeholder")}
            ref={selectorRef}
            showArrow={showArrow && !locked}
            size={size}
            {...selectProps}
          />
          {locked && (
            <div className={block("lock")}>
              <Tooltip title={lockTooltip ?? null}>
                <FontAwesomeIcon icon={faLock} />
              </Tooltip>
            </div>
          )}
        </div>
        {error ? (
          <ErrorRow>{error}</ErrorRow>
        ) : caption ? (
          <div className={block("caption")}>{caption}</div>
        ) : null}
      </div>
    </Labeled>
  );
};

export interface SelectFieldComponent {
  blur(): void;
  focus: () => void;
}

export default forwardRef(SelectField) as <T extends SelectValue = SelectValue>(
  props: SelectFieldProps<T> & { ref?: React.Ref<SelectFieldComponent> }
) => React.ReactElement;
